import React, { JSX, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { getSvg } from "../common/utils/getSvg";
import { EstablishmentMenu } from "../common/components/EstablishmentMenu";
import { AccountButton } from "../common/components/AccountButton";
import { Button, ListItem } from "@hatch-digital-nl/abs-storybook/legacy";
import { useAppDispatch, useAppLocation, useAppSelector } from "./hooks";
import { setIsCreateCertificateModalOpen } from "../modals/CreateCertificateModal/CreateCertificateModalSlice";
import { useIsEstablishmentManager } from "../common/utils/useIsEstablishmentManager";
import { useGetEmployeeMeQuery } from "../common/api/employee";
import Skeleton from "react-loading-skeleton";
import { getInitials } from "../common/utils/getInitials";
import { useIsEstablishmentSet } from "../common/utils/useIsEstablishmentSet";
import { getActiveEstablishment } from "../common/utils/getActiveEstablishment";
import { useGetEstablishmentsQuery } from "../common/api/establishment";
import { useEmployeeId } from "../common/utils/useEmployeeId";
import { setIsPickEstablishmentModalOpen } from "../modals/PickEstablishmentModal/PickEstablishmentModalSlice";
import { RouteEnum } from "../common/enums/RouteEnum";
import { EmployeeMenu } from "../common/components/EmployeeMenu";
import { LoadModals } from "../common/components/LoadModals";
import { setIsMenuOpen } from "./rootReducer";
import { getUserFullName } from "../common/utils/getUserFullName";
import { redirectToPortalWebLogout } from "../common/utils/portalWebLogout";
import { AuthContext, IAuthContext } from "react-oauth2-code-pkce";

interface Props {
    children?: React.ReactNode;
}
export const AppWrapper = (props: Props): JSX.Element => {
    const { idToken } = useContext<IAuthContext>(AuthContext);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const currentLocation = useAppLocation();
    const [location, setLocation] = useState(window.location.pathname);
    const employeeId = useEmployeeId();
    const isEstablishmentSet = useIsEstablishmentSet();
    const isEstablishmentManager = useIsEstablishmentManager();
    const { isMenuOpen } = useAppSelector((state) => state.root);
    const { data: employeeMeData } = useGetEmployeeMeQuery({});
    const { data: establishmentData, isLoading: establishmentDataIsLoading } =
        useGetEstablishmentsQuery({
            items_per_page: 9999,
            filter: {
                employee_id: employeeId,
            },
        });
    const moreThenOneEstablishment = establishmentData
        ? establishmentData.data.length > 1
        : false;
    const activeEstablishment = establishmentData
        ? getActiveEstablishment(establishmentData.data, isEstablishmentSet)
        : null;

    useEffect(() => {
        if (
            moreThenOneEstablishment &&
            isEstablishmentManager &&
            !activeEstablishment
        ) {
            dispatch(setIsPickEstablishmentModalOpen(true));
        }
    }, [
        dispatch,
        moreThenOneEstablishment,
        isEstablishmentManager,
        activeEstablishment,
    ]);

    if (currentLocation.pathname === "/") {
        navigate(RouteEnum.DASHBOARD);
    }

    return (
        <section className={"l-app"}>
            <section className="l-app__mobile-header">
                <button
                    type="button"
                    className={"l-app__mobile-header__toggle"}
                    aria-expanded={isMenuOpen}
                    aria-controls="app-menu"
                    aria-label="Menu"
                    onClick={() => {
                        dispatch(setIsMenuOpen(!isMenuOpen));
                    }}
                >
                    <span />
                    <span />
                    <span />
                </button>

                {getSvg("AbsLogo")}

                <AccountButton initials={"JD"} />
            </section>

            <aside
                id="app-menu"
                className={isMenuOpen ? "l-app__menu is-open" : "l-app__menu"}
            >
                <div className="l-app__menu__content">
                    <Link to={"/"} className="l-app__logo">
                        {getSvg("AbsLogo")}
                    </Link>

                    {establishmentDataIsLoading ? (
                        <Skeleton
                            count={1}
                            height={"5rem"}
                            borderRadius={"0.8rem"}
                        />
                    ) : (
                        <>
                            {isEstablishmentManager &&
                                moreThenOneEstablishment &&
                                activeEstablishment && (
                                    <div>
                                        <ListItem
                                            key={activeEstablishment.id}
                                            className={
                                                "l-app__menu__content__selector"
                                            }
                                            establishment={activeEstablishment}
                                            onClick={() => {
                                                dispatch(
                                                    setIsPickEstablishmentModalOpen(
                                                        true,
                                                    ),
                                                );
                                            }}
                                        />
                                    </div>
                                )}

                            {!moreThenOneEstablishment && (
                                <Button
                                    variant={"primary"}
                                    onClick={() => {
                                        dispatch(
                                            setIsCreateCertificateModalOpen(
                                                true,
                                            ),
                                        );
                                    }}
                                >
                                    + Certificaat toevoegen
                                </Button>
                            )}
                        </>
                    )}

                    {isEstablishmentManager ? (
                        <EstablishmentMenu
                            location={location}
                            setLocation={setLocation}
                        />
                    ) : (
                        <EmployeeMenu
                            location={location}
                            setLocation={setLocation}
                        />
                    )}
                </div>
                <div className="l-app__menu__content">
                    <AccountButton
                        initials={getInitials(
                            getUserFullName(employeeMeData?.user) ?? undefined,
                        )}
                        fullName={
                            getUserFullName(employeeMeData?.user) ?? undefined
                        }
                        company={employeeMeData?.establishment?.name}
                    />
                    <button
                        type="button"
                        className={"l-app__menu__content__logout"}
                        onClick={() => redirectToPortalWebLogout(idToken)}
                    >
                        uitloggen
                    </button>
                </div>
                <button
                    type="button"
                    className={"l-app__menu__overlay"}
                    aria-label="Menu sluiten"
                    onClick={() => {
                        dispatch(setIsMenuOpen(false));
                    }}
                />
            </aside>
            <main className="l-app__content">
                {props.children}
                <LoadModals />
            </main>
        </section>
    );
};
